import React, { useEffect, useState } from 'react';
import Calling from '../../api/Calling';
import { toast } from 'react-toastify';
import Loader from '../../components/Loader';
import generateCaptcha from '../../components/captcha';

const Suggestion = () => {
    const [loader, setLoader] = useState(false);
    const [data, setData] = useState([]);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [selectAll, setSelectAll] = useState(false);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [viewItem, setViewItem] = useState(null);
    const limit = 50;

    const fetchData = async (pageNumber = 1) => {
        try {
            setLoader(true);
            const resp = await Calling.getSuggestion(pageNumber, limit);
            if (resp?.status) {
                setData(resp.data || []);
                setPage(resp.page || 1);
                setTotalPages(resp.totalPages || 1);
                setSelectedUsers([]);
                setSelectAll(false);
            } else {
                toast.error(resp?.message || 'Failed to load suggestions');
            }
        } catch (error) {
            console.error(error);
            toast.error('Server error while fetching suggestions');
        } finally {
            setLoader(false);
        }
    };

    useEffect(() => {
        fetchData(page);
    }, [page]);


    const handleSelect = (id) => {
        if (selectedUsers.includes(id)) {
            setSelectedUsers(selectedUsers.filter(item => item !== id));
        } else {
            setSelectedUsers([...selectedUsers, id]);
        }
    };

    const handleDeleteSelected = async (id = null) => {
        const content = `You are about to delete a Record. To confirm, please solve this math problem.`;
        const check = generateCaptcha(content);

        if (!check) {
            toast.error("Captcha answer is incorrect", { autoClose: 2000 });
            return;
        }

        try {
            setLoader(true);
            const body = { id: id ? [id] : selectedUsers };
            const response = await Calling.deleteSuggestion(body);

            if (response?.status) {
                toast.success(response.message || 'Record(s) deleted successfully!');

                // Remove deleted items from local state first (optimistic update)
                const deletedIds = id ? [id] : selectedUsers;
                const newData = data.filter(item => !deletedIds.includes(item._id));

                // If no items left on this page, go to previous page
                if (newData.length === 0 && page > 1) {
                    setPage(prev => prev - 1);
                } else {
                    setData(newData);
                }

                // Reset selection
                setSelectedUsers([]);
                setSelectAll(false);
            } else {
                toast.error(response?.message || 'Failed to delete record(s)');
            }
        } catch (error) {
            console.error('Error deleting suggestions:', error);
            toast.error('Server error while deleting record(s)');
        } finally {
            setLoader(false);
        }
    };


    if (loader) return <Loader loader={loader} />;

    return (
        <div className="px-3 sm:px-6 lg:ml-[4.4rem] w-full mt-16 md:mt-20 bg-main-bg mb-4">
            {/* Header */}
            <div className="bg-main-white w-full shadow-lg rounded-lg overflow-hidden flex justify-between items-center p-4 mb-4">
                <h1 className="text-2xl font-bold text-gray-800">Suggestion Center</h1>
                {data.length > 0 && (
                    <button
                        className="bg-red-500 px-4 py-2 rounded text-white text-sm hover:bg-red-600 transition flex items-center gap-2 disabled:opacity-50"
                        onClick={() => handleDeleteSelected()}
                        disabled={selectedUsers.length === 0}
                    >
                        Delete Selected
                    </button>
                )}
            </div>

            {/* Table */}
            <div className="flex flex-col gap-3">
                {data.map(item => (
                    <div
                        key={item._id}
                        className="bg-white shadow-md rounded-lg p-4 flex flex-col sm:flex-row sm:justify-between items-start sm:items-center gap-2 hover:shadow-lg transition"
                    >
                        <div className="flex items-center gap-3">
                            <input
                                type="checkbox"
                                checked={selectedUsers.includes(item._id)}
                                onChange={() => handleSelect(item._id)}
                            />
                            <div className="flex flex-col">
                                <span className="font-medium text-gray-800">{item.name}</span>
                                <span className="text-gray-500 text-sm">{item.email}</span>
                            </div>
                        </div>
                        <div className="text-gray-500 text-sm">{new Date(item.createdAt).toLocaleString()}</div>
                        <div className="flex gap-2 mt-2 sm:mt-0">
                            <button
                                className="bg-blue-500 px-3 py-1 rounded text-white text-sm hover:bg-blue-600 transition"
                                onClick={() => setViewItem(item)}
                            >
                                View
                            </button>
                            <button
                                className="bg-red-500 px-3 py-1 rounded text-white text-sm hover:bg-red-600 transition"
                                onClick={() => handleDeleteSelected(item._id)}
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                ))}
            </div>

            {/* Pagination */}
            <div className="flex justify-center items-center gap-3 mt-6">
                <button
                    className="px-3 py-1 border rounded disabled:opacity-50 hover:bg-gray-100 transition"
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                >
                    Prev
                </button>
                <span className="text-gray-700">Page {page} of {totalPages}</span>
                <button
                    className="px-3 py-1 border rounded disabled:opacity-50 hover:bg-gray-100 transition"
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                >
                    Next
                </button>
            </div>

            {/* Modal for viewing suggestion */}
            {viewItem && (
                <div className="fixed inset-0 bg-black/50 flex justify-center items-center z-50 p-4">
                    <div className="bg-white rounded-lg p-6 max-w-lg w-full relative overflow-y-auto max-h-[80vh] shadow-lg">
                        <h2 className="text-xl font-bold mb-2 text-gray-800">Suggestion Details</h2>
                        <p className="mb-1"><strong>Name:</strong> {viewItem.name}</p>
                        <p className="mb-1"><strong>Email:</strong> {viewItem.email}</p>
                        <p className="mb-2"><strong>Message:</strong> {viewItem.msg}</p>
                        <p className="text-gray-500 text-sm"><strong>Created At:</strong> {new Date(viewItem.createdAt).toLocaleString()}</p>
                        <button
                            className="absolute top-2 right-2 bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600"
                            onClick={() => setViewItem(null)}
                        >
                            Close
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Suggestion;
